import React from "react";
import { X } from "lucide-react";

interface VoicePanelShellProps {
  children: React.ReactNode;
  /** Renders the top-right close control. Omit to hide it (nothing to cancel yet). */
  onClose?: () => void;
  closeLabel?: string;
}

/**
 * The dark panel every voice step is drawn on: it takes the place of the
 * tabs+search card, so it keeps that card's width and corner radius and only
 * swaps the contents between steps (listening, confirm, questions).
 *
 * The gold arcs are decorative and pointer-events-none — they are drawn in
 * SVG rather than exported as a PNG so they stay crisp at any panel width.
 */
const VoicePanelShell: React.FC<VoicePanelShellProps> = ({
  children,
  onClose,
  closeLabel = "Close voice search",
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#080808] shadow-2xl min-h-[196px] flex items-center justify-center">
    {/* Decorative light streaks — top-left sweep and top-right glints. */}
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 1632 212"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="voice-arc-warm" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFD980" />
          <stop offset="45%" stopColor="#FFBC00" />
          <stop offset="100%" stopColor="#FFBC00" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="voice-arc-bloom" cx="0.12" cy="0" r="0.55">
          <stop offset="0%" stopColor="#FFBC00" stopOpacity="0.45" />
          <stop offset="60%" stopColor="#B87A00" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>
        <filter id="voice-arc-blur" x="-20%" y="-40%" width="140%" height="200%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      <rect width="1632" height="212" fill="url(#voice-arc-bloom)" />

      <g fill="none" filter="url(#voice-arc-blur)">
        <path d="M -90 178 C 90 40 300 -30 470 -70" stroke="url(#voice-arc-warm)" strokeWidth="14" />
      </g>
      <g fill="none" stroke="url(#voice-arc-warm)">
        <path d="M -90 178 C 90 40 300 -30 470 -70" strokeWidth="3" opacity="0.95" />
        <path d="M -110 214 C 70 78 290 6 460 -34" strokeWidth="1.2" opacity="0.55" />
        <path d="M -60 132 C 110 14 320 -50 500 -92" strokeWidth="1" opacity="0.35" />
        <path d="M 1720 -40 C 1610 40 1520 96 1430 150" strokeWidth="1.6" opacity="0.5" />
        <path d="M 1760 -20 C 1650 66 1560 124 1470 180" strokeWidth="1" opacity="0.3" />
      </g>

      <g fill="#FFBC00">
        <circle cx="1268" cy="66" r="2" opacity="0.55" />
        <circle cx="1104" cy="126" r="1.4" opacity="0.35" />
        <circle cx="446" cy="58" r="1.6" opacity="0.4" />
        <circle cx="1512" cy="128" r="1.6" opacity="0.4" />
      </g>
    </svg>

    {onClose && (
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white/80 transition-colors hover:bg-white/20 hover:text-white cursor-pointer"
      >
        <X className="h-4 w-4" />
      </button>
    )}

    <div className="relative z-10 w-full px-5 py-6 sm:px-8">{children}</div>
  </div>
);

export default VoicePanelShell;
